import { ComponentProps } from "react";

import { Link, useLoaderData } from "@remix-run/react";
import { ActionFunctionArgs, LoaderFunctionArgs, MetaFunction, redirect } from "@vercel/remix";
import { ShoppingCartIcon } from "lucide-react";

import { BackLink, Button } from "~/components";
import { PaymentSummary } from "~/components/shared/OrderSummary";
// import { CustomerLinks } from "~/components/shared/ToolBar";
import { TombstoneIcon } from "~/icons";
import { cn } from "~/lib/utils";
import { CartWithPackagesAndSpaces, deleteCartPackage, deleteSpaceCart, getCartFromCookie, getCustomerFromCookie } from "~/models";
import { destroyCartSession } from "~/models/cart_session.server";
import { useTrackLinks } from "~/utils/analytics";

import { DELETE_CART_PACKAGE, PackageDisplay } from "./PackageDisplay";
import { DELETE_CART_SPACE, SpaceDisplay } from "./SpaceDisplay";
import { analyticsServer } from "~/utils/analytics.server";
import { getCartPrice } from "~/lib/cart";
import DATA from "~/data";

const CONTACT_VENDOR_THRESHOLD = 5000000;

export async function loader({ request }: LoaderFunctionArgs) {
    const cart = await getCartFromCookie({ request });
    const { customer, withAuthKit } = await getCustomerFromCookie({ request });

    let fromPage = null;
    try {
        const referrer = new URL(request.headers.get("Referer") || "");
        fromPage = referrer.pathname;
    } catch { }

    const analytics = analyticsServer(request);
    if (fromPage && fromPage?.includes("/immediate-need/map")) {
        analytics.track("Immediate Need - Map to Cart Conversion");
    }

    if (fromPage && fromPage?.includes("/plan-for-the-future/map")) {
        analytics.track("Plan for the Future - Map to Cart Conversion");
    }

    return {
        cart,
        customer,
        withAuthKit,
        fromPage,
        baseUrl: process.env.BASE_URL,
    };
}

export const meta: MetaFunction = () => {
    return [
        { title: "Cart | Our Guidant" },
        {
            name: "description",
            content:
                "Review your selected items, update quantities, and proceed to checkout on our secure cart page. Enjoy a seamless shopping experience with easy modifications and fast checkout options.",
        },
    ];
};

export default function Cart() {
    const { cart, customer, withAuthKit, baseUrl, fromPage } = useLoaderData<typeof loader>();

    useTrackLinks("#checkout", "Cart Checkout", {
        cartId: cart?.id,
        customerId: customer?.id,
        spaces: cart?.spaces?.length.toString(),
        packages: cart?.packages?.length.toString(),
    });

    let hasSpaces = false;
    let hasPackages = false;
    if (cart) {
        hasSpaces = cart.spaces.length > 0;
        hasPackages = cart.packages.length > 0;
    }
    const needsOrderSummary = hasSpaces || hasPackages;

    const cartAmounts = cart ? getCartPrice(cart) : null;
    const needsToContactVendor = cartAmounts?.total && cartAmounts?.total > CONTACT_VENDOR_THRESHOLD

    return (
        <section className="flex flex-col gap-8 p-8 page">
            <div className="flex flex-wrap justify-between items-center gap-2">
                {/* {!fromPage || fromPage == "/cart" ? <div /> : <BackButton fromPage={fromPage} />} */}
                <BackButton fromPage={fromPage} />
                {/* <CustomerLinks customer={customer} withAuthKit={withAuthKit} /> */}
            </div>
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-title">My Cart</h1>
            </div>
            {!needsOrderSummary ? (
                <div className="border border-gray-200 p-4 flex flex-col justify-center items-center rounded-md min-h-[300px] gap-4">
                    <ShoppingCartIcon className="w-10 h-10" />
                    <div className="flex flex-col items-center gap-2">
                        <div className="text-xl font-semibold">Your cart is empty</div>
                    </div>
                    <Button className="!px-6" asChild>
                        <Link to="/map">Browse Available Spaces</Link>
                    </Button>
                </div>
            ) : (
                <div className="flex flex-col sm:grid grid-cols-8 gap-12">
                    <div className="col-span-full sm:col-span-4 md:col-span-5 lg:col-span-6 flex flex-col gap-8">
                        {cart.spaces.map((cartSpace) => {
                            return (
                                <CartItem key={cartSpace.id} className="first:pt-0">
                                    <SpaceDisplay
                                        key={cartSpace.id}
                                        cartSpace={cartSpace}
                                        baseUrl={baseUrl}
                                        cart={cart}
                                    />
                                </CartItem>
                            );
                        })}
                        {cart.packages.map((cartPackage) => {
                            return (
                                <CartItem key={cartPackage.id} className="first:pt-0">
                                    <PackageDisplay key={cartPackage.id} cartPackage={cartPackage} />
                                </CartItem>
                            );
                        })}
                    </div>
                    <aside className="col-span-3 sm:col-span-4 md:col-span-3 lg:col-span-2 flex flex-col gap-8 sticky w-screen -ml-8 sm:w-auto sm:ml-0 right-0 left-0 bg-white px-6 py-4 bottom-0 border-t border-border sm:static sm:px-0 sm:py-0 sm:border-0">
                        <PaymentSummary cart={cart} />
                        {!needsToContactVendor ? (
                            <Link id="checkout" to="/checkout">
                                <Button className="w-full">Checkout</Button>
                            </Link>
                        ) : (
                            <ContactVendorPrompt />
                        )}
                        {!hasSpaces && <Prompt />}
                    </aside>
                </div>
            )}
        </section>
    );
}

export async function action({ request }: ActionFunctionArgs) {
    const body = await request.formData();
    const intent = body.get("intent");

    switch (intent) {
        case DELETE_CART_SPACE:
            const cartSpaceId = body.get("cartSpaceId") as string;
            await deleteSpaceCart(cartSpaceId);
            break;

        case DELETE_CART_PACKAGE:
            const cartPackageId = body.get("cartPackageId") as string;
            await deleteCartPackage(cartPackageId);
            break;
    }

    const cart = await getCartFromCookie({ request });
    if (cart && cart.spaces.length === 0 && cart.packages.length === 0) {
        const headers = new Headers();
        headers.append("Set-Cookie", await destroyCartSession(request));
        return redirect("/cart", { headers });
    }

    return null;
}

export function BackButton({ fromPage }: { fromPage: string | null }) {
    let child = null;

    if (fromPage && fromPage.endsWith("/map")) {
        child = "return to map";
    } else if (fromPage && fromPage.startsWith("/packages")) {
        child = "return to package choices";
    } else {
        return <BackLink className="uppercase">Back</BackLink>;
    }

    return child ? (
        <BackLink to={fromPage} className="uppercase">
            {child}
        </BackLink>
    ) : (
        <div />
    );
}

function CartItem({ className, ...props }: ComponentProps<"section">) {
    return (
        <section
            className={cn("border-border border-t first:border-t-0 pt-8 last:border-b last:pb-8", className)}
            {...props}
        />
    );
}

function ContactVendorPrompt() {
    return (
        <section className="flex flex-col justify-center items-center gap-8 border-2 border-zinc-300 py-4 px-8 rounded">
            <section className="flex flex-col items-center gap-2">
                <h2 className="text-xl font-semibold text-center ">Contact Us To Complete Your Purchase</h2>
                <p className="text-sm text-center">For cart totals above $50,000, a team member will assist you with finalizing your purchase by phone.</p>
            </section>
            <Button variant="link" size="lg" className="w-full">
                <a href={`tel:${DATA.tollFree}`} className="underline">{DATA.tollFree}</a>
            </Button>
        </section>
    );
}

function Prompt() {
    return (
        <section className="flex flex-col justify-center items-center gap-8 border-2 border-zinc-300 p-8 rounded">
            <section className="flex flex-col items-center gap-2">
                <TombstoneIcon className="w-10 h-10" />
                <h2 className="text-2xl font-semibold text-center">Do you need a memorial space ?</h2>
            </section>
            <Link to="/map">
                <Button>View spaces</Button>
            </Link>
        </section>
    );
}
